import showdown from 'showdown';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- no types for sanitize-html
import sanitizeHtml from 'sanitize-html';
import TurndownService from 'turndown';

export const MENTION_CLASS = 'markdown-mention';

const SANITIZE_CFG = {
	allowedTags: [
		'p',
		'br',
		'hr',
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'strong',
		'em',
		'b',
		'i',
		'u',
		's',
		'del',
		'ul',
		'ol',
		'li',
		'a',
		'blockquote',
		'pre',
		'code',
		'table',
		'thead',
		'tbody',
		'tr',
		'td',
		'th',
		'input',
		'span'
	],
	allowedAttributes: {
		a: ['href', 'target', 'rel'],
		td: ['align'],
		th: ['align'],
		input: ['type', 'checked', 'disabled']
	},
	allowedClasses: { span: [MENTION_CLASS] },
	allowedSchemes: ['http', 'https', 'mailto', 'tel']
};

const BASE_OPTIONS = {
	tables: true,
	simplifiedAutoLink: true,
	strikethrough: true,
	tasklists: true,
	openLinksInNewWindow: true,
	ghCodeBlocks: true
};

const aiConverter = new showdown.Converter(BASE_OPTIONS);

const lineBreakConverter = new showdown.Converter({ ...BASE_OPTIONS, simpleLineBreaks: true });

// Text a user typed years before markdown rendering existed must come out unchanged, so mid-word
// `snake_case` / `2*5000` stay literal and every single newline becomes a <br> instead of collapsing.
const userConverter = new showdown.Converter({
	...BASE_OPTIONS,
	simpleLineBreaks: true,
	literalMidWordUnderscores: true,
	literalMidWordAsterisks: true,
	noHeaderId: true
});

export const renderMarkdown = (text: string): string => sanitizeHtml(aiConverter.makeHtml(text), SANITIZE_CFG);

/**
 * Same as renderMarkdown but treats a single newline as a hard line break. Use for content authored
 * as plain prose with meaningful line breaks (e.g. outreach email bodies whose sign-off is
 * "Best,\n<name>") — the default converter would otherwise collapse that newline into a space
 * (WEB-8801). For text a person typed into Upsales use renderUserMarkdown instead, which also keeps
 * mid-word punctuation literal.
 */
export const renderMarkdownWithLineBreaks = (text: string): string =>
	sanitizeHtml(lineBreakConverter.makeHtml(text), SANITIZE_CFG);

export const renderUserMarkdown = (text: string): string => sanitizeHtml(userConverter.makeHtml(text), SANITIZE_CFG);

/** Strip markdown to a single plain-text line for the row preview. */
export const previewText = (md: string | null | undefined): string => {
	if (!md) return '';
	return md
		.replace(/```[\s\S]*?```/g, ' ')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/^\s*[-+*]\s+/gm, '')
		.replace(/^\s*\d+\.\s+/gm, '')
		.replace(/[*_`#>]/g, '')
		.replace(/\s+/g, ' ')
		.trim();
};

const isBold = (node: HTMLElement) => parseInt(node.style.fontWeight, 10) >= 600;
const isItalic = (node: HTMLElement) => node.style.fontStyle === 'italic';

const cellText = (cell: Element) => (cell.textContent ?? '').replace(/\s+/g, ' ').replace(/\|/g, '\\|').trim();

const tableToMarkdown = (table: HTMLElement) => {
	const rows = Array.from(table.querySelectorAll('tr')).map(row => Array.from(row.children).map(cellText));
	if (!rows.length) {
		return '';
	}
	const width = Math.max(...rows.map(row => row.length));
	const toLine = (cells: string[]) => `| ${Array.from({ length: width }, (_, i) => cells[i] ?? '').join(' | ')} |`;
	const [header, ...body] = rows;
	const separator = `| ${Array(width).fill('---').join(' | ')} |`;
	return `\n\n${[toLine(header), separator, ...body.map(toLine)].join('\n')}\n\n`;
};

const turndownService = new TurndownService({
	headingStyle: 'atx',
	bulletListMarker: '-',
	codeBlockStyle: 'fenced'
})
	// Google Docs wraps its whole payload in <b style="font-weight:normal">, which reads as bold
	.addRule('googleDocsWrapper', {
		filter: node => node.nodeName === 'B' && node.style.fontWeight === 'normal',
		replacement: content => content
	})
	// Docs and Sheets carry emphasis as inline styles rather than <strong>/<em>
	.addRule('styledSpan', {
		filter: node => node.nodeName === 'SPAN' && (isBold(node) || isItalic(node)),
		replacement: (content, node) => {
			const element = node as HTMLElement;
			if (!content.trim()) {
				return content;
			}
			const bold = isBold(element) ? `**${content}**` : content;
			return isItalic(element) ? `_${bold}_` : bold;
		}
	})
	// Turndown has no table rule, and without one every cell becomes its own paragraph
	.addRule('table', {
		filter: 'table',
		replacement: (_content, node) => tableToMarkdown(node as HTMLElement)
	})
	// Images cannot survive the sanitizer, so keep the alt text instead of dropping them silently
	.addRule('imageAsText', {
		filter: 'img',
		replacement: (_content, node) => (node as HTMLElement).getAttribute('alt') ?? ''
	})
	// Script and style bodies are plain text to turndown, so their source would land in the note
	.remove(['script', 'style', 'noscript']);

export const htmlToMarkdown = (html: string): string => turndownService.turndown(html);

const decodeHtmlEntities = (text: string) => {
	const textarea = document.createElement('textarea');
	textarea.innerHTML = text;
	return textarea.value;
};

/**
 * Comments created from email or tickets are stored as HTML. Both the editor and the read view must
 * flatten it the same way, or a comment would render differently depending on whether you are
 * editing it. Deliberately not run through turndown: that would promote email signature tables to
 * real tables, which is louder than how these have always rendered.
 */
export const flattenStoredHtml = (text: string): string =>
	decodeHtmlEntities(
		text
			.replace(/<br\s*\/?>/gi, '\n')
			.replace(/<\/?(p|div|li|ul|ol|h[1-6]|blockquote)[^>]*>/gi, '\n')
			.replace(/<[^>]+>/g, '')
	);
